import { createSelector } from '@reduxjs/toolkit';
import { isFuture, isPast, isWithinInterval, parseISO } from 'date-fns';

import { RootState } from '../../redux/root-reducer';
import { State } from './event-detail-types';

const selectEventDetails = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => eventDetails,
);

export const selectEventSlug = createSelector(
  [selectEventDetails],
  eventDetails => eventDetails.event.url,
);

const getEventCurrencyCode = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => eventDetails.event.currency,
);

const getIsAfterRegistrationCloseTime = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    !eventDetails.settings ||
    isPast(parseISO(eventDetails.settings.registrationCloseTime)),
);

const getIsQuestionBankEnabled = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => Boolean(eventDetails.settings.questionBankEnabled),
);

const getIsAuthenticationEnabled = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => Boolean(eventDetails.settings.authEnabled),
);

const getCustomRegistrationClosedMessage = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails.settings && eventDetails.settings.registrationCloseMessage,
);

const getMaxTicketsPerOrder = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails.settings && eventDetails.settings.maxTicketsPerOrder,
);

const getRegistrationsLimitReached = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    !eventDetails.settings ||
    (eventDetails.settings.maxRegistrations !== null &&
      eventDetails.event.totalUsersRegistered >=
        eventDetails.settings.maxRegistrations),
);

const getEventHasEnded = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => isPast(parseISO(eventDetails.event.endTime)),
);

const getWaitlistEnabled = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails.settings && eventDetails.settings.waitlistEnabled,
);

const getGlobalWaitlistLimitReached = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => {
    const {
      settings: { maxWaitlist } = {},
      event: { totalUsersWaitlisted } = {},
    } = eventDetails;
    return maxWaitlist && totalUsersWaitlisted
      ? totalUsersWaitlisted >= maxWaitlist
      : false;
  },
);

const getWaitlistDateClosed = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails.settings && eventDetails.settings.waitlistCloseTime
      ? isPast(parseISO(eventDetails.settings.waitlistCloseTime))
      : false,
);

const getEventExternalId = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails?.event?.externalId ? eventDetails?.event?.externalId : null,
);

const getSuccessfulWaitlistMessage = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails.settings && eventDetails.settings.successfulWaitlistMessage,
);

const getUnsuccessfulWaitlistMessage = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails =>
    eventDetails.settings && eventDetails.settings.unsuccessfulWaitlistMessage,
);

const getStripeMerchantId = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => eventDetails.event.organizationMerchantId,
);

const getIsUsingStripeDirect = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => eventDetails.stripeDirect,
);

const getGoogleAnalyticsCode = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => eventDetails.googleAnalyticsCode,
);

const getTrackingIntegrations = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => eventDetails.trackingIntegrations,
);

export const getEventData = createSelector(
  selectEventDetails,
  (eventDetails: State) => eventDetails.event,
);
export const areDoorsOpen = createSelector(getEventData, event => {
  const { doorsOpenTime, doorsCloseTime } = event;

  if (!doorsOpenTime || !doorsCloseTime) {
    return false;
  }

  const openTimeISO = parseISO(doorsOpenTime);
  const closeTimeISO = parseISO(doorsCloseTime);

  return isWithinInterval(Date.now(), {
    start: openTimeISO,
    end: closeTimeISO,
  });
});

export const isFirstTicketClosed = (eventDetails: State) => {
  if (!eventDetails?.tickets[0]) {
    return false;
  }
  const {
    salesStartTime,
    salesEndTime,
    registrationsLimit,
    currentRegistrations,
    status,
  } = eventDetails.tickets[0];
  const salesDateInvalid =
    salesEndTime &&
    salesStartTime &&
    (isFuture(parseISO(salesStartTime)) || isPast(parseISO(salesEndTime)));
  const closeDateHasPast =
    !eventDetails.settings ||
    isPast(parseISO(eventDetails.settings.registrationCloseTime));
  const regLimitReached =
    !eventDetails.settings ||
    (eventDetails.settings.maxRegistrations !== null &&
      eventDetails.event.totalUsersRegistered >=
        eventDetails.settings.maxRegistrations);
  const eventHasEnded = isPast(parseISO(eventDetails.event.endTime));

  const ticketRegLimitReached = registrationsLimit
    ? currentRegistrations >= registrationsLimit
    : false;

  return (
    status === 'closed' ||
    salesDateInvalid ||
    closeDateHasPast ||
    regLimitReached ||
    eventHasEnded ||
    ticketRegLimitReached
  );
};

export const isFreeEventSelector = createSelector(
  (state: RootState) => state.eventDetails.tickets,
  tickets => {
    return tickets.every(ticket => ticket.price === 0);
  },
);

export const getIsRegistrationClosed = () =>
  createSelector(
    [
      getIsPostEventRegistrationClosed,
      getEventHasEnded,
      getIsAfterRegistrationCloseTime,
      getRegistrationsLimitReached,
      selectEventDetails,
    ],
    (
      isPostEventRegistrationClosed,
      eventHasEnded,
      isAfterRegistrationCloseTime,
      registrationsLimitReached,
      { event: { postEventEnabled } },
    ) => {
      const isRegistrationEnded = postEventEnabled
        ? isPostEventRegistrationClosed
        : eventHasEnded;

      return Boolean(
        isRegistrationEnded ||
          isAfterRegistrationCloseTime ||
          registrationsLimitReached,
      );
    },
  );

export const getIsPostEventRegistrationClosed = createSelector(
  (state: RootState) => state.eventDetails.event,
  ({ postEventEnabled, postEventTimeEnd }) =>
    Boolean(
      !postEventEnabled ||
        !postEventTimeEnd ||
        isPast(parseISO(String(postEventTimeEnd))),
    ),
);

export const isFirstTicketClosedSelector = createSelector(
  (state: RootState) => state,
  state => {
    return isFirstTicketClosed(state.eventDetails);
  },
);

export const isWaitlistEnabledForFirstTicketSelector = createSelector(
  (state: RootState) => state,
  (state: RootState) => {
    if (!state.eventDetails?.tickets[0]) {
      return false;
    }
    const {
      currentWaitlistCount,
      waitlistCount,
      registrationsLimit,
      currentRegistrations,
    } = state.eventDetails.tickets[0];
    const waitlistEnabled = getWaitlistEnabled(state);
    const waitlistClosed = getWaitlistDateClosed(state);
    const globalMaxWaitlistLimitReached = getGlobalWaitlistLimitReached(state);

    const ticketWaitlistCountReached = waitlistCount
      ? currentWaitlistCount >= waitlistCount
      : false;

    const ticketRegLimitReached = registrationsLimit
      ? currentRegistrations >= registrationsLimit
      : false;

    return (
      !waitlistClosed &&
      waitlistEnabled &&
      ticketRegLimitReached &&
      !ticketWaitlistCountReached &&
      !globalMaxWaitlistLimitReached
    );
  },
);

export const isLiveEventSelector = createSelector(
  [selectEventDetails],
  ({ event }) => {
    // for backward compatability
    if (event.doorsOpenTime && event.doorsCloseTime) {
      return (
        isPast(new Date(event.doorsOpenTime)) &&
        !isPast(new Date(event.doorsCloseTime))
      );
    }
    return (
      isPast(new Date(event.startTime)) && !isPast(new Date(event.endTime))
    );
  },
);

const getShowEventSeries = createSelector(
  (state: RootState) => state.eventDetails,
  eventDetails => Boolean(eventDetails.showEventSeries),
);

export {
  selectEventDetails,
  getEventCurrencyCode,
  getIsAfterRegistrationCloseTime,
  getCustomRegistrationClosedMessage,
  getRegistrationsLimitReached,
  getEventHasEnded,
  getEventExternalId,
  getWaitlistEnabled,
  getGlobalWaitlistLimitReached,
  getWaitlistDateClosed,
  getSuccessfulWaitlistMessage,
  getMaxTicketsPerOrder,
  getUnsuccessfulWaitlistMessage,
  getStripeMerchantId,
  getIsUsingStripeDirect,
  getGoogleAnalyticsCode,
  getIsAuthenticationEnabled,
  getIsQuestionBankEnabled,
  getTrackingIntegrations,
  getShowEventSeries,
};
